import { Box, Tab, Tabs, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { StepConfig } from './useCampaignSteps';

interface StepsBarProps {
  steps: readonly StepConfig[];
  activeStep: number;
  onChange: (index: number) => void;
  compact: boolean;
  // Tant qu'il est renseigné (ex. aucun canal choisi), seule la première étape est accessible ; les autres sont
  // grisées et affichent ce message au survol
  lockedReason?: string | null;
}

// Étapes de la campagne en barre horizontale, dans l'en-tête de l'éditeur, pour laisser toute la largeur au contenu
function StepsBar({ steps, activeStep, onChange, compact, lockedReason = null }: StepsBarProps) {
  const theme = useTheme();
  const lockedColor = theme.palette.grey[400];
  const isLocked = (index: number) => lockedReason !== null && index > 0;

  return (
    <Tabs
      value={activeStep}
      onChange={(_, index: number) => { if (!isLocked(index)) onChange(index); }}
      // Grand écran : les étapes se partagent toute la largeur disponible ; petit écran : barre défilante
      variant={compact ? 'scrollable' : 'fullWidth'}
      scrollButtons={compact ? 'auto' : false}
      allowScrollButtonsMobile={compact}
      sx={{
        width: '100%',
        minHeight: 48,
        '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3 },
        '& .MuiTab-root': {
          minHeight: 48,
          minWidth: compact ? 48 : 0,
          maxWidth: 'none',
          px: compact ? 1 : 1.5,
          textTransform: 'none',
          color: 'text.secondary',
          fontWeight: 500,
          '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
        },
      }}
    >
      {steps.map(({ key, label, Icon }, index) => {
        const locked = isLocked(index);
        const iconColor = locked ? lockedColor : activeStep === index ? 'primary.main' : 'text.disabled';
        // Un onglet désactivé ne reçoit pas le survol : on le garde actif mais inerte pour afficher l'explication
        const content = (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Icon fontSize="small" sx={{ color: iconColor }} />
            {!compact && label}
          </Box>
        );
        return (
          <Tab
            key={key}
            aria-label={compact ? label : undefined}
            aria-disabled={locked || undefined}
            disableRipple={locked}
            // Même structure verrouillé ou non (titre vide = pas d'infobulle) : sinon le span inline ajoute une
            // hauteur de ligne et les onglets se décalent verticalement au choix du canal
            label={(
              <Tooltip title={locked ? lockedReason : ''} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>{content}</Box>
              </Tooltip>
            )}
            sx={locked ? { color: `${lockedColor} !important`, cursor: 'not-allowed' } : undefined}
          />
        );
      })}
    </Tabs>
  );
}

export default StepsBar;
